import { useCallback, useEffect, useRef, useState } from "react";
import SearchBox, { FileGlyph } from "./SearchBox";
import ResultsGrid from "./ResultsGrid";
import BriefView from "./BriefView";
import GateScreen from "./GateScreen";
import RequestNotice from "./RequestNotice";
import { apiFetch, isUnlocked } from "./apiConfig";
import { isOverRequestTextLimit, shouldAnalyse } from "./requestText";
import "./app.css";

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // mirrors backend/briefRoutes.js's multer limits.fileSize — keep this comment
const NOTE_NO_NEED = "Couldn't pick out a design need, searched your text as written.";
const NOTE_ANALYSIS_FAILED = "Couldn't analyse your text right now; searched it as written.";
const NOTE_REPORT_NOT_STARTED = "Couldn't start the report right now; searched your text as written.";
// Shown when a several-needs outcome could not be turned into a report (issue #23).
const NOTE_REPORT_QUOTA = "You've reached the hourly limit for reports; searched your text as written.";
const NOTE_REPORT_TOO_MANY_ATTEMPTS = "Too many attempts right now; searched your text as written.";
const NOTE_REPORT_BUSY = "Reports are busy right now; searched your text as written.";
// Request text that would be analysed can be up to 20,000 characters, but a plain
// search embeds the whole query as one input and token-dense text can exceed the
// embedding model's input limit. Only this many leading characters are searched.
const PLAIN_SEARCH_MAX_CHARS = 2000;
// A few seconds over the server's 20 s analysis deadline, so a hung server or
// proxy cannot leave the search box disabled.
const ANALYSIS_TIMEOUT_MS = 25000;
// Starting the report is a quick request (the job runs in the background).
const START_REPORT_TIMEOUT_MS = 15000;
// Export title for results of a plain search of Request text (never the text itself).
const PASTED_TEXT_EXPORT_LABEL = "Pasted text";
const MAX_ATTACHED_FILES = 1; // multi-doc seam for later — bump this + add `multiple` to the input when that's built

/** Formats a byte count as a human-readable KB/MB string, e.g. "240 KB" or "1.3 MB".
 * Duplicated from BriefView.jsx (not exported there) — small enough not to be
 * worth a shared module for now. */
function formatFileSize(bytes) {
  if (!Number.isFinite(bytes) || bytes < 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  const mb = kb / 1024;
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

function validateFile(file) {
  const name = (file.name || "").toLowerCase();
  if (!name.endsWith(".txt") && !name.endsWith(".pdf")) return "That file type isn't supported — attach a .txt or .pdf.";
  if (file.size === 0) return "That file is empty.";
  if (file.size > MAX_UPLOAD_BYTES) return `That file is ${formatFileSize(file.size)} — the limit is 10 MB.`;
  return null;
}

function hasFilesInDrag(e) {
  return Array.from(e.dataTransfer?.types || []).includes("Files");
}

export default function App() {
  const [unlocked, setUnlocked] = useState(isUnlocked);

  const [query, setQuery] = useState("");
  const [activeQuery, setActiveQuery] = useState("");
  const [results, setResults] = useState([]);
  const [corpusFreshness, setCorpusFreshness] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchVersion, setSearchVersion] = useState(0);
  const [isAnalysing, setIsAnalysing] = useState(false);
  const [understoodAs, setUnderstoodAs] = useState(null); // null | { title, description } from a one-need analysis
  const [searchNote, setSearchNote] = useState(null); // why the text was searched as written, when analysis didn't answer

  const [attachedFiles, setAttachedFiles] = useState([]);
  const [reportPurpose, setReportPurpose] = useState("");
  const [attachError, setAttachError] = useState(null);
  const [briefRun, setBriefRun] = useState(null); // null | { files, purpose, runKey } (upload) | { startedJob, runKey } (Request text)
  const [dragActive, setDragActive] = useState(false);

  const dragDepthRef = useRef(0);
  const fileInputRef = useRef(null);
  const reportPurposeInputRef = useRef(null);
  const uploadDropzoneRef = useRef(null);
  const filePickerRef = useRef(null);
  const reportViewRef = useRef(null);
  const focusAttachedPanelRef = useRef(false);
  const focusUploadAffordanceRef = useRef(false);
  const focusPurposeOnReturnRef = useRef(false);
  const searchRequestRef = useRef(0);
  const analysisControllerRef = useRef(null);

  const mode = briefRun ? "brief" : hasSearched ? "results" : "landing";

  // Starts a new search or analysis: supersedes any request in flight and
  // resets what the results area shows. Returns the id the caller checks for staleness.
  const beginSearch = useCallback((activeText, { analysing }) => {
    analysisControllerRef.current?.abort(); // a superseded analysis is cancelled, not just ignored
    const requestId = searchRequestRef.current + 1;
    searchRequestRef.current = requestId;
    setSearchVersion(requestId);
    setActiveQuery(activeText);
    setError(null);
    setUnderstoodAs(null);
    setSearchNote(null);
    setHasSearched(true);
    setIsAnalysing(analysing);
    setIsLoading(true);
    return requestId;
  }, []);

  // Plain search of `q` as written. `note` (optional) says why it ran instead of an analysis.
  const runPlainSearch = useCallback(async (q, requestId, note = null) => {
    setIsAnalysing(false);
    try {
      const res = await apiFetch("/api/search", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: shouldAnalyse(q) ? q.slice(0, PLAIN_SEARCH_MAX_CHARS).trimEnd() : q }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) {
        throw new Error(
          typeof data.message === "string" && data.message
            ? data.message
            : `Search failed (${res.status})`,
        );
      }
      if (requestId !== searchRequestRef.current) return;
      setResults(Array.isArray(data.results) ? data.results : []);
      setCorpusFreshness(data.corpusFreshness || null);
      setSearchNote(note);
    } catch (e) {
      if (requestId !== searchRequestRef.current) return;
      setError(e?.message || "Something went wrong");
      setResults([]);
      setCorpusFreshness(null);
    } finally {
      if (requestId === searchRequestRef.current) setIsLoading(false);
    }
  }, []);

  const handleSearch = useCallback(
    (rawQuery) => {
      const q = typeof rawQuery === "string" ? rawQuery.trim() : "";
      const requestId = beginSearch(q, { analysing: false });
      setQuery(q);
      return runPlainSearch(q, requestId);
    },
    [beginSearch, runPlainSearch],
  );

  // Asks the server to start a report from the Design needs it holds for
  // `analysisId` (never sent from here). Resolves { job } when the report has
  // started, { note } when the text should be searched as written instead, or
  // null when a newer search has superseded this one.
  const startReport = useCallback(async (analysisId, requestId) => {
    const controller = new AbortController();
    analysisControllerRef.current = controller;
    const timeoutId = setTimeout(() => controller.abort(), START_REPORT_TIMEOUT_MS);
    try {
      const res = await apiFetch("/api/brief/from-text", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ analysisId }),
        signal: controller.signal,
      });
      const data = await res.json().catch(() => ({}));
      if (requestId !== searchRequestRef.current) return null;
      if (res.ok && data.ok !== false && typeof data.jobId === "string" && typeof data.streamToken === "string") {
        return { job: { jobId: data.jobId, streamToken: data.streamToken } };
      }
      if (res.status === 429) {
        // Two limits answer 429: the hourly allowance of reports, and a generic cap on attempts.
        return { note: data.code === "report_quota_spent" ? NOTE_REPORT_QUOTA : NOTE_REPORT_TOO_MANY_ATTEMPTS };
      }
      if (res.status === 503 && data.code === "busy") return { note: NOTE_REPORT_BUSY };
      return { note: NOTE_REPORT_NOT_STARTED }; // expired analysis, report not configured, anything else
    } catch {
      if (requestId !== searchRequestRef.current) return null;
      return { note: NOTE_REPORT_NOT_STARTED }; // network failure or the client-side time limit
    } finally {
      clearTimeout(timeoutId);
      if (analysisControllerRef.current === controller) analysisControllerRef.current = null;
    }
  }, []);

  // Request text longer or more varied than a quick query goes to the analysis
  // endpoint. The box keeps the user's original text. Whatever the outcome, the
  // user ends up with results (or the normal empty state): anything but a
  // usable one-need answer falls back to a plain search of the text as written.
  const handleAnalyse = useCallback(
    async (rawText) => {
      const text = rawText.trim();
      const requestId = beginSearch(text, { analysing: true });
      const controller = new AbortController();
      analysisControllerRef.current = controller;
      const timeoutId = setTimeout(() => controller.abort(), ANALYSIS_TIMEOUT_MS);
      let analysis = null;
      let analysisFailed = false;
      try {
        const res = await apiFetch("/api/request/analyze", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ text }),
          signal: controller.signal,
        });
        const data = await res.json().catch(() => ({}));
        // Superseded by a newer search (which aborted this call): no fallback.
        if (requestId !== searchRequestRef.current) return;
        // An auth failure would fail the plain search too: surface it as search does today.
        if (res.status === 401) {
          setError(typeof data.message === "string" && data.message ? data.message : `Search failed (${res.status})`);
          setResults([]);
          setCorpusFreshness(null);
          setIsAnalysing(false);
          setIsLoading(false);
          return;
        }
        if (res.ok && data.ok !== false && !controller.signal.aborted) analysis = data;
        else analysisFailed = true;
      } catch {
        if (requestId !== searchRequestRef.current) return;
        analysisFailed = true; // network failure or the client-side time limit
      } finally {
        clearTimeout(timeoutId);
        if (analysisControllerRef.current === controller) analysisControllerRef.current = null;
      }

      const title = analysis?.understoodAs?.title;
      if (analysis?.outcome === "one" && typeof title === "string" && title.trim() && Array.isArray(analysis.results)) {
        const description = analysis.understoodAs.description;
        setUnderstoodAs({ title: title.trim(), description: typeof description === "string" ? description.trim() : "" });
        setResults(analysis.results);
        setCorpusFreshness(analysis.corpusFreshness || null);
        setIsAnalysing(false);
        setIsLoading(false);
        return;
      }

      // SEVERAL NEEDS: start the report from the analysis the server holds, and
      // move to the report view. The "Reading your text…" state stays up until
      // then; if the report cannot start, the text is searched as written.
      let note = analysisFailed
        ? NOTE_ANALYSIS_FAILED
        : analysis?.outcome === "several"
          ? NOTE_REPORT_NOT_STARTED // several needs but no usable analysis id
          : NOTE_NO_NEED;
      if (analysis?.outcome === "several" && typeof analysis.analysisId === "string" && analysis.analysisId) {
        const started = await startReport(analysis.analysisId, requestId);
        if (!started) return; // superseded by a newer search
        if (started.job) {
          setIsAnalysing(false);
          setIsLoading(false);
          focusPurposeOnReturnRef.current = false;
          setBriefRun({
            startedJob: started.job,
            runKey: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          });
          return;
        }
        note = started.note;
      }
      await runPlainSearch(text, requestId, note);
    },
    [beginSearch, runPlainSearch, startReport],
  );

  const handleSubmit = useCallback(() => {
    if (isOverRequestTextLimit(query)) return;
    if (shouldAnalyse(query)) handleAnalyse(query);
    else handleSearch(query);
  }, [query, handleAnalyse, handleSearch]);

  const handleNewSearch = useCallback(() => {
    analysisControllerRef.current?.abort();
    searchRequestRef.current += 1;
    setHasSearched(false);
    setQuery("");
    setActiveQuery("");
    setResults([]);
    setCorpusFreshness(null);
    setAttachedFiles([]);
    setReportPurpose("");
    setAttachError(null);
    focusAttachedPanelRef.current = false;
    focusUploadAffordanceRef.current = false;
    dragDepthRef.current = 0;
    setDragActive(false);
    setError(null);
    setUnderstoodAs(null);
    setSearchNote(null);
    setIsAnalysing(false);
    setIsLoading(false);
  }, []);

  const handleExampleChip = useCallback(
    (text) => {
      setQuery(text);
      handleSearch(text);
    },
    [handleSearch],
  );

  function attachFiles(list) {
    if (briefRun || isLoading) return; // no file mutations during a report or search operation
    for (const f of list) {
      const err = validateFile(f);
      if (err) {
        setAttachError(err);
        return;
      }
    }
    setAttachError(null);
    focusAttachedPanelRef.current = true;
    focusUploadAffordanceRef.current = false;
    setReportPurpose("");
    setAttachedFiles(list.slice(0, MAX_ATTACHED_FILES));
  }

  function handleRemoveFile(index) {
    focusAttachedPanelRef.current = false;
    focusUploadAffordanceRef.current = true;
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
    setReportPurpose("");
    setAttachError(null);
  }

  function handleFileInputChange(e) {
    attachFiles(Array.from(e.target.files || []));
    e.target.value = ""; // allow re-picking the same file
  }

  function handleReportSubmit() {
    if (isLoading || !attachedFiles.length) return;
    setBriefRun({
      files: attachedFiles,
      purpose: reportPurpose.trim(),
      runKey: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    });
  }

  function handleBriefRetry() {
    setBriefRun((r) =>
      r ? { ...r, runKey: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}` } : r,
    );
  }

  function handleBriefStartNew() {
    focusPurposeOnReturnRef.current = false;
    setBriefRun(null);
    handleNewSearch();
  }

  function handleBriefBack() {
    const fromText = !!briefRun?.startedJob;
    focusPurposeOnReturnRef.current = !fromText; // a text run has no purpose field to return to
    if (fromText) {
      // Back to the box with the text still in it, not to the stale results of an earlier search.
      setHasSearched(false);
      setActiveQuery("");
      setResults([]);
      setCorpusFreshness(null);
      setSearchNote(null);
    }
    setBriefRun(null); // deliberately do NOT clear attachedFiles/query/reportPurpose — user can retry or swap the file
  }

  function handleDragEnter(e) {
    if (briefRun || isLoading || !hasFilesInDrag(e)) return;
    e.preventDefault();
    dragDepthRef.current += 1;
    if (dragDepthRef.current === 1) setDragActive(true);
  }
  function handleDragOver(e) {
    if (!hasFilesInDrag(e)) return;
    e.preventDefault(); // always for a file-drag, or the browser opens the file — even mid-run
    e.dataTransfer.dropEffect = briefRun || isLoading ? "none" : "copy";
  }
  function handleDragLeave(e) {
    if (briefRun || isLoading) {
      dragDepthRef.current = 0;
      setDragActive(false);
      return;
    }
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setDragActive(false);
    }
  }
  function handleDrop(e) {
    if (!hasFilesInDrag(e)) return; // not a file drag — don't touch attachedFiles or drag-active state
    e.preventDefault();
    dragDepthRef.current = 0;
    setDragActive(false);
    if (briefRun || isLoading) return; // safe no-op now that preventDefault already ran
    attachFiles(Array.from(e.dataTransfer?.files || []));
  }

  useEffect(() => {
    if (attachedFiles.length > 0 && focusAttachedPanelRef.current) {
      if (reportPurposeInputRef.current) {
        reportPurposeInputRef.current.focus();
        focusAttachedPanelRef.current = false;
      }
      return;
    }
    if (attachedFiles.length === 0 && focusUploadAffordanceRef.current) {
      const target = uploadDropzoneRef.current || filePickerRef.current;
      if (!target || target.disabled) return;
      target.focus();
      focusUploadAffordanceRef.current = false;
    }
  }, [attachedFiles, hasSearched, isLoading]);

  useEffect(() => {
    if (!isLoading && !briefRun) return;
    dragDepthRef.current = 0;
    setDragActive(false);
  }, [isLoading, briefRun]);

  useEffect(() => {
    if (briefRun) {
      reportViewRef.current?.focus();
      return;
    }
    if (focusPurposeOnReturnRef.current) {
      focusPurposeOnReturnRef.current = false;
      reportPurposeInputRef.current?.focus();
    }
  }, [briefRun]);

  useEffect(() => {
    const reset = () => {
      dragDepthRef.current = 0;
      setDragActive(false);
    };
    window.addEventListener("dragend", reset);
    window.addEventListener("drop", reset);
    return () => {
      window.removeEventListener("dragend", reset);
      window.removeEventListener("drop", reset);
    };
  }, []);

  if (!unlocked) {
    return <GateScreen onUnlock={() => setUnlocked(true)} />;
  }

  return (
    <div
      className="app-shell"
      data-has-searched={hasSearched ? "true" : "false"}
      data-mode={mode}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".txt,.pdf,application/pdf,text/plain"
        disabled={isLoading || !!briefRun}
        hidden
        onChange={handleFileInputChange}
      />

      <header className="gd-nav" data-theme="dark">
        <h1 className="gd-nav__brand app-nav__brand">Figma Reference Finder</h1>
        <p className="small gd-muted app-nav__tagline">Find the best existing screens for your next project</p>
      </header>

      <div className={mode === "landing" ? "gd-page" : "gd-page gd-page--wide"}>
        {briefRun ? (
          <div
            ref={reportViewRef}
            tabIndex={-1}
            role="region"
            aria-label={`Report for ${briefRun.startedJob ? "pasted text" : briefRun.files[0]?.name || "attached document"}`}
          >
            <BriefView
              key={briefRun.runKey}
              file={briefRun.files?.[0]}
              purpose={briefRun.purpose}
              startedJob={briefRun.startedJob}
              onRetry={briefRun.startedJob ? undefined : handleBriefRetry}
              onStartNew={handleBriefStartNew}
              onBack={handleBriefBack}
            />
          </div>
        ) : (
          <>
            <SearchBox
              variant={hasSearched ? "results" : "empty"}
              query={query}
              onQueryChange={setQuery}
              onSubmit={handleSubmit}
              onNewSearch={handleNewSearch}
              onExampleChip={handleExampleChip}
              isLoading={isLoading}
              isAnalysing={isAnalysing}
              attachedFiles={attachedFiles}
              reportPurpose={reportPurpose}
              reportPurposeInputRef={reportPurposeInputRef}
              uploadDropzoneRef={uploadDropzoneRef}
              filePickerRef={filePickerRef}
              onReportPurposeChange={setReportPurpose}
              onGenerateReport={handleReportSubmit}
              onOpenFilePicker={() => fileInputRef.current?.click()}
              onRemoveFile={handleRemoveFile}
              attachError={attachError}
              dragActive={dragActive}
            />

            {hasSearched && !isLoading ? (
              <RequestNotice
                understoodAs={understoodAs}
                note={searchNote}
              />
            ) : null}

            {hasSearched ? (
              <ResultsGrid
                results={results}
                activeQuery={understoodAs ? understoodAs.title : activeQuery}
                exportLabel={
                  understoodAs ? understoodAs.title : shouldAnalyse(activeQuery) ? PASTED_TEXT_EXPORT_LABEL : activeQuery
                }
                isLoading={isLoading}
                error={error}
                searchVersion={searchVersion}
                corpusFreshness={corpusFreshness}
              />
            ) : null}
          </>
        )}
      </div>

      {dragActive && mode !== "brief" && !isLoading ? (
        <div className="gd-scrim app-drop-overlay" aria-hidden="true">
          <div className="gd-empty app-drop-card">
            <div className="gd-empty__mark app-mark">
              <FileGlyph />
            </div>
            <p className="gd-empty__title">Drop your requirements doc</p>
            <p className="gd-empty__text">.txt or .pdf · up to 10 MB</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
